import { ContentAlertListResponse, ContentAlertResolveResponse, ContentCollectorRunResponse, ContentHealthResponse, ContentOverrideCreateResponse, ContentOverviewResponse, ContentPublicationDetailResponse, ContentPublishSettingUpdateResponse, ContentPublishSettingsResponse, ContentReportListResponse, ContentRuleUpdateResponse, ContentRulesResponse, } from '@wit-support/admin-contracts';
import { createModuleApi } from '@wit-support/admin-client-core';
const api = createModuleApi({ errorFormat: 'server-message' });
export function setContentApiBaseUrl(url) {
    api.setBaseUrl(url);
}
/**
 * Адрес медиа admin-api (`/api/content/media/...` из DTO) для `<video>`/`<img>`:
 * относительный путь приклеивается к адресу admin-api, как у запросов
 * (сессия-кука уходит браузером). null — медиа нет.
 */
export function contentMediaSrc(path) {
    if (!path)
        return null;
    return `${api.baseUrl()}${path}`;
}
/**
 * Ошибка запроса с СОХРАНЁННЫМ кодом ответа и машинным кодом сервера.
 *
 * Экран принимает по коду разные решения: 403 объясняется словами (раздел
 * виден всей команде, а менять настройки могут супервайзер и владелец),
 * 503 `content_engine_not_configured` — «не подключён», 502/503 — «движок
 * недоступен». Разбирать это по подстроке сообщения нельзя: текст меняется,
 * код — нет.
 */
export class ContentRequestError extends Error {
    status;
    /** `error` из тела ответа (`upstream_rejected`, `content_engine_not_configured`, …). */
    code;
    /**
     * `code` из тела ответа — машинный код движка при `upstream_rejected`
     * (`longform_not_enabled`, `validation_failed`, …), как
     * `ModuleRequestError.upstreamCode`. null — admin-api кода не прислал.
     */
    upstreamCode;
    constructor(message, status, code = null, upstreamCode = null) {
        super(message);
        this.name = 'ContentRequestError';
        this.status = status;
        this.code = code;
        this.upstreamCode = upstreamCode;
    }
}
/**
 * Тело ошибки admin-api: `{ error, code?, message? }`. Для `upstream_rejected`
 * человеку нужен `message` движка («аккаунт не найден»), а не машинный код.
 */
const GENERIC_REJECTED_TEXT = 'Движок не принял запрос. Проверьте значения и повторите.';
async function toError(r) {
    const text = await r.text().catch(() => '');
    let error = null;
    let message = null;
    let upstreamCode = null;
    try {
        const body = JSON.parse(text);
        if (typeof body.error === 'string')
            error = body.error;
        if (typeof body.message === 'string')
            message = body.message;
        if (typeof body.code === 'string' && body.code !== '')
            upstreamCode = body.code;
    }
    catch {
        // Не JSON (прокси, HTML-страница ошибки) — останется текст ответа.
    }
    return new ContentRequestError(
    // `message` — деловая русская фраза admin-api (по коду движка); без неё
    // `error` — машинное слово, поэтому для upstream_rejected оно не показывается.
    message ??
        (error === 'upstream_rejected' ? GENERIC_REJECTED_TEXT : error) ??
        (text || r.statusText || `HTTP ${r.status}`), r.status, error, upstreamCode);
}
function jsonInit(method, body) {
    return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
}
/**
 * Запрос + разбор ответа СХЕМОЙ контракта: значения по умолчанию
 * (`.nullable().default(null)`) проставляются здесь, и экран читает уже
 * полную форму, даже если старый admin-api поля ещё не отдаёт.
 */
async function request(schema, path, init) {
    const r = await api.fetchRaw(path, init);
    if (!r.ok)
        throw await toError(r);
    return schema.parse(await r.json());
}
function qs(params) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params))
        if (v !== undefined && v !== '')
            p.set(k, v);
    const s = p.toString();
    return s ? `?${s}` : '';
}
const BASE = '/api/content';
/** GET /health — подключён ли движок, последний сбор, открытые тревоги. */
export function getContentHealth() {
    return request(ContentHealthResponse, `${BASE}/health`);
}
/** GET /overview — таблица «аккаунт × сеть». */
export function getContentOverview() {
    return request(ContentOverviewResponse, `${BASE}/overview`);
}
/** GET /publications/:id — карточка публикации. */
export function getPublication(id) {
    return request(ContentPublicationDetailResponse, `${BASE}/publications/${encodeURIComponent(id)}`);
}
/** POST /overrides — «Не ставить первым» / «Ставить первым» / «Отдых». */
export function createOverride(body) {
    return request(ContentOverrideCreateResponse, `${BASE}/overrides`, jsonInit('POST', body));
}
/** DELETE /overrides/:id — снять переопределение. Тело ответа не нужно. */
export async function deleteOverride(id) {
    const r = await api.fetchRaw(`${BASE}/overrides/${encodeURIComponent(id)}`, {
        method: 'DELETE',
    });
    if (!r.ok)
        throw await toError(r);
}
/** GET /publish-settings — подписи и хэштеги по аккаунтам. */
export function getPublishSettings() {
    return request(ContentPublishSettingsResponse, `${BASE}/publish-settings`);
}
/** PUT /publish-settings — пустая `caption` возвращает подпись из конфига. */
export function updateCaption(body) {
    return request(ContentPublishSettingUpdateResponse, `${BASE}/publish-settings`, jsonInit('PUT', body));
}
/** GET /rules — правило выбора первого клипа по каждой сети. */
export function getRules() {
    return request(ContentRulesResponse, `${BASE}/rules`);
}
/** PUT /rules — сменить правило; `auto` снимает ручной выбор. */
export function updateRule(body) {
    return request(ContentRuleUpdateResponse, `${BASE}/rules`, jsonInit('PUT', body));
}
/** PUT /rules/params — три понятных параметра сети. */
export function updateRuleParams(body) {
    return request(ContentRuleUpdateResponse, `${BASE}/rules/params`, jsonInit('PUT', body));
}
/**
 * GET /alerts. `resolved: false` — только открытые; без параметра — все
 * (открытые и решённые).
 */
export function listAlerts(opts = {}) {
    return request(ContentAlertListResponse, `${BASE}/alerts${qs({ resolved: opts.resolved === undefined ? undefined : String(opts.resolved) })}`);
}
/** POST /alerts/:id/resolve — «Решено». */
export function resolveAlert(id) {
    return request(ContentAlertResolveResponse, `${BASE}/alerts/${encodeURIComponent(id)}/resolve`, {
        method: 'POST',
    });
}
/** GET /reports — последние недельные отчёты. */
export function listReports(limit) {
    return request(ContentReportListResponse, `${BASE}/reports${qs({ limit: limit === undefined ? undefined : String(limit) })}`);
}
/** POST /collector/run — ручной запуск сбора статистики. */
export function runCollector() {
    return request(ContentCollectorRunResponse, `${BASE}/collector/run`, { method: 'POST' });
}
/**
 * Общий транспорт для файлов запросов по областям (`client/api/<область>.ts`):
 * тот же клиент, разбор схемой и сборка query-string, что у запросов выше.
 */
export { request as contentRequest, qs as contentQs, jsonInit as contentJsonInit, BASE as CONTENT_API_BASE, };
//# sourceMappingURL=api.js.map